import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '../hooks/useSettings';
import { FaMoneyBillWave, FaMapMarkerAlt, FaGift, FaBox, FaPlus, FaEdit, FaTrash, FaHandshake, FaTools, FaPhoneAlt, FaCloudDownloadAlt } from 'react-icons/fa';
import DropImage from '../components/DropImage';
import '../styles/settings.scss';
import { useRepairShops } from '../hooks/useRepairShops';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const markerIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});

const ClickHandler = ({ onSelect }) => {
    useMapEvents({
        click(e) {
            onSelect({ latitude: e.latlng.lat, longitude: e.latlng.lng });
        },
    });
    return null;
};

const DEFAULT_SIGNUP_TERMS = `1) منصة جاهز وسيط رقمي يربط العميل بالسائق ولا تشغل أو تملك مركبات.
2) العلاقة تعاقدية مباشرة بين العميل والسائق، وكل طرف مسؤول قانونياً عن التزاماته.
3) الشركة غير مسؤولة عن فقدان أو سرقة المركبة أو المقتنيات أو أضرار ميكانيكية/كهربائية تحدث أثناء النقل.
4) السائق يقر بامتلاك رخصة قيادة ورخصة مزاولة سارية ويتحمل أي مسؤولية قانونية أو جزائية.
5) العميل يقر بملكية المركبة أو تفويض قانوني، وألا يستخدم التطبيق لأغراض مخالفة للقانون.
6) يحظر نقل مركبات مسروقة، أو مواد محظورة، أو التحايل على نقاط التفتيش، أو أي نشاط يخل بالأمن.
7) لا تتحمل الشركة مسؤولية النزاعات المالية بين العميل والسائق، وفي حال أي تعويض لا يتجاوز الحد قيمة الرسوم أو العمولة للعملية محل النزاع.
8) يلتزم المستخدم بتعويض الشركة عن أي أضرار أو دعاوى تنشأ عن استخدامه للتطبيق بالمخالفة للقانون.
9) يحق للشركة جمع البيانات اللازمة للتشغيل وفق الخصوصية، والإفصاح للجهات المختصة إذا طُلب رسمياً.
10) يحق للشركة إيقاف أو إلغاء الحساب أو تعديل الشروط في أي وقت.
11) تختص المحاكم الليبية بأي نزاع وينطبق القانون الليبي.`;

export const Settings = () => {
    const user = useSelector(state => state.userController.user);
    const navigate = useNavigate();
    const API_BASE = process.env.REACT_APP_API_BASE_URL;
    const {
        settings,
        packages,
        isLoading,
        updatePriceRanges,
        updateMaxSearchRange,
        updateReferralPrize,
        updateJoiningPrize,
        updateDriverSearchConfig,
        updateEmergencyNumbers,
        updateAppUpdateConfig,
        updateAppTermsConfig,
        createPackage,
        updatePackage,
        deletePackage
    } = useSettings();

    const {
        shops: repairShops,
        isLoading: repairShopsLoading,
        saving: repairShopsSaving,
        saveShop,
        deleteShop,
    } = useRepairShops();

    const [activeModal, setActiveModal] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    // Price ranges state
    const [priceRanges, setPriceRanges] = useState([]);

    // Emergency numbers state
    const [emergencyNumbers, setEmergencyNumbers] = useState([]);

    // Max search range state
    const [maxSearchRange, setMaxSearchRange] = useState('');

    // Referral prize state
    const [referralPrize, setReferralPrize] = useState('');

    // Joining prize state
    const [joiningPrizeEnabled, setJoiningPrizeEnabled] = useState(false);

    // App update enforcement state
    const [appUpdateConfig, setAppUpdateConfig] = useState({
        forceUpdate: false,
        message: 'يوجد تحديث إجباري للتطبيق. يرجى التحديث للمتابعة.',
        android: { minimumVersion: '', minimumBuild: '', storeUrl: '' },
        ios: { minimumVersion: '', minimumBuild: '', storeUrl: '' },
    });

    // Signup terms configuration (separate user/driver)
    const [appTermsConfig, setAppTermsConfig] = useState({
        userTerms: DEFAULT_SIGNUP_TERMS,
        driverTerms: DEFAULT_SIGNUP_TERMS,
    });

    // Driver search config state
    const [driverSearchConfig, setDriverSearchConfig] = useState({
        initialDriverBatch: 15,
        nextDriverBatch: 5,
        nextDriverDelaySeconds: 20,
    });

    // Repair shops state
    const [repairForm, setRepairForm] = useState({ name: '', address: '', specialties: '', phone: '', latitude: '', longitude: '', mapUrl: '' });
    const [locationQuery, setLocationQuery] = useState('');
    const [locationSuggestions, setLocationSuggestions] = useState([]);
    const [locationHint, setLocationHint] = useState('');
    const [locationLoading, setLocationLoading] = useState(false);
    const [mapCenter, setMapCenter] = useState([32.8872, 13.1913]); // default Tripoli
    const [editingRepairId, setEditingRepairId] = useState(null);

    // Package state
    const [packageForm, setPackageForm] = useState({
        _id: null,
        name: '',
        packageType: 'standard',
        price: '',
        durationInDays: '',
        maxTrips: '',
        isThereDiscount: false,
        priceAfterDiscount: '',
        packageImage: '',
        description: '',
        badgeLabel: '',
        statusText: '',
        statusTone: 'info',
        ctaText: 'اشتراك الان',
        isActive: true
    });
    const [packageImageFile, setPackageImageFile] = useState([]);

    const emergencyCount = useMemo(() => emergencyNumbers.filter((n) => n.name && n.phoneNumber).length, [emergencyNumbers]);

    useEffect(() => {
        if (!user) {
            navigate('/login');
        }
    }, [user, navigate]);

    useEffect(() => {
        if (settings) {
            setPriceRanges(settings.priceRanges || []);
            setMaxSearchRange(settings.maxSearchRangeKm || '');
            setReferralPrize(settings.referralPrize || '');
            setJoiningPrizeEnabled(Boolean(settings.joiningPrizeEnabled));
            const list = Array.isArray(settings.emergencyNumbers) && settings.emergencyNumbers.length > 0
                ? settings.emergencyNumbers
                : [{ name: '', phoneNumber: '' }];
            setEmergencyNumbers(list);
            setAppUpdateConfig({
                forceUpdate: Boolean(settings?.appUpdate?.forceUpdate),
                message: settings?.appUpdate?.message || 'يوجد تحديث إجباري للتطبيق. يرجى التحديث للمتابعة.',
                android: {
                    minimumVersion: settings?.appUpdate?.android?.minimumVersion || '',
                    minimumBuild: settings?.appUpdate?.android?.minimumBuild || '',
                    storeUrl: settings?.appUpdate?.android?.storeUrl || '',
                },
                ios: {
                    minimumVersion: settings?.appUpdate?.ios?.minimumVersion || '',
                    minimumBuild: settings?.appUpdate?.ios?.minimumBuild || '',
                    storeUrl: settings?.appUpdate?.ios?.storeUrl || '',
                },
            });
            setAppTermsConfig({
                userTerms: settings?.appTerms?.userTerms || DEFAULT_SIGNUP_TERMS,
                driverTerms: settings?.appTerms?.driverTerms || DEFAULT_SIGNUP_TERMS,
            });
            setDriverSearchConfig({
                initialDriverBatch: settings?.initialDriverBatch ?? 15,
                nextDriverBatch: settings?.nextDriverBatch ?? 5,
                nextDriverDelaySeconds: settings?.nextDriverDelaySeconds ?? 20,
            });
        }
    }, [settings]);

    const openModal = (modalName) => {
        setActiveModal(modalName);
    };

    const closeModal = () => {
        setActiveModal(null);
        setPackageForm({
            _id: null,
            name: '',
            packageType: 'standard',
            price: '',
            durationInDays: '',
            maxTrips: '',
            isThereDiscount: false,
            priceAfterDiscount: '',
            packageImage: '',
            description: '',
            badgeLabel: '',
            statusText: '',
            statusTone: 'info',
            ctaText: 'اشتراك الان',
            isActive: true
        });
        setPackageImageFile([]);
        setRepairForm({ name: '', address: '', specialties: '', phone: '', latitude: '', longitude: '', mapUrl: '' });
        setLocationQuery('');
        setLocationSuggestions([]);
        setEditingRepairId(null);
    };

    const startEditRepair = (shop) => {
        setEditingRepairId(shop._id);
        setRepairForm({
            name: shop.name || '',
            address: shop.address || '',
            specialties: shop.specialties || '',
            phone: shop.phone || '',
            latitude: shop?.location?.latitude ?? '',
            longitude: shop?.location?.longitude ?? '',
            mapUrl: '',
        });
        if (shop?.location?.latitude && shop?.location?.longitude) {
            setMapCenter([shop.location.latitude, shop.location.longitude]);
        }
        openModal('repairShops');
    };

    const setCoords = ({ latitude, longitude }) => {
        setRepairForm((prev) => ({ ...prev, latitude, longitude }));
        setMapCenter([latitude, longitude]);
    };

    const searchLocations = async (query) => {
        const trimmed = query.trim();
        setLocationQuery(query);
        if (trimmed.length < 3) {
            setLocationSuggestions([]);
            setLocationHint('');
            setLocationLoading(false);
            return;
        }
        if (!user?.token || !API_BASE) {
            setLocationHint('تأكد من تسجيل الدخول وإعداد API_BASE_URL');
            setLocationSuggestions([]);
            return;
        }
        setLocationLoading(true);
        setLocationSuggestions([]);
        setLocationHint('');
        try {
            const params = new URLSearchParams({ input: trimmed });
            if (mapCenter && Array.isArray(mapCenter) && Number.isFinite(mapCenter[0]) && Number.isFinite(mapCenter[1])) {
                params.append('lat', mapCenter[0]);
                params.append('lng', mapCenter[1]);
            }
            const res = await fetch(`${API_BASE}/api/admin/settings/location-autocomplete?${params.toString()}`, {
                headers: {
                    authorization: `bearer ${user.token}`,
                },
            });
            const data = await res.json();
            if (!res.ok) {
                setLocationSuggestions([]);
                setLocationHint(data?.error || 'تعذر جلب النتائج حاليًا');
                return;
            }
            const suggestions = Array.isArray(data)
                ? data
                : Array.isArray(data?.predictions)
                    ? data.predictions
                    : [];
            if (suggestions.length === 0) {
                setLocationHint('لا توجد نتائج. جرّب اسمًا مختلفًا بالعربي أو الإنجليزي.');
            } else {
                setLocationHint('');
            }
            setLocationSuggestions(suggestions);
        } catch (err) {
            setLocationSuggestions([]);
            setLocationHint('تعذر جلب النتائج حاليًا');
        } finally {
            setLocationLoading(false);
        }
    };

    const handleSelectSuggestion = async (item) => {
        if (!item?.placeId || !user?.token || !API_BASE) return;
        try {
            const res = await fetch(`${API_BASE}/api/admin/settings/location-details?placeId=${encodeURIComponent(item.placeId)}`, {
                headers: {
                    authorization: `bearer ${user.token}`,
                },
            });
            const data = await res.json();
            if (!res.ok) {
                setLocationHint(data?.error || 'تعذر جلب تفاصيل الموقع');
                return;
            }
            if (Number.isFinite(data?.latitude) && Number.isFinite(data?.longitude)) {
                const display = data?.address || item.description || item.mainText || locationQuery;
                setCoords({ latitude: data.latitude, longitude: data.longitude });
                setRepairForm((prev) => ({ ...prev, address: prev.address || display, mapUrl: data?.mapUrl || prev.mapUrl }));
                setLocationSuggestions([]);
                setLocationQuery(display || '');
            }
        } catch (err) {
            // ignore
        }
    };

    const handleSaveRepairShop = async (e) => {
        e.preventDefault();
        const latNum = Number(repairForm.latitude);
        const lngNum = Number(repairForm.longitude);
        if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) {
            return;
        }

        const payload = {
            name: repairForm.name.trim(),
            address: (repairForm.address || '').trim(),
            specialties: (repairForm.specialties || '').trim() || undefined,
            phone: (repairForm.phone || '').trim() || undefined,
            latitude: latNum,
            longitude: lngNum,
            mapUrl: (repairForm.mapUrl || '').trim() || undefined,
        };

        const ok = await saveShop(payload, editingRepairId);
        if (ok) {
            setRepairForm({ name: '', address: '', specialties: '', phone: '', latitude: '', longitude: '', mapUrl: '' });
            setEditingRepairId(null);
            setLocationQuery('');
            setLocationSuggestions([]);
        }
    };

    // Price Ranges handlers
    const addPriceRange = () => {
        setPriceRanges([...priceRanges, { fromKm: 0, toKm: 0, startingPrice: 0, pricePerKm: 0 }]);
    };

    const removePriceRange = (index) => {
        setPriceRanges(priceRanges.filter((_, i) => i !== index));
    };

    const updatePriceRange = (index, field, value) => {
        const updated = [...priceRanges];
        updated[index][field] = parseFloat(value) || 0;
        setPriceRanges(updated);
    };

    const handleSavePriceRanges = async () => {
        try {
            setModalLoading(true);
            await updatePriceRanges(priceRanges);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Max Search Range handlers
    const handleSaveMaxSearchRange = async () => {
        try {
            setModalLoading(true);
            await updateMaxSearchRange(parseFloat(maxSearchRange));
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Referral Prize handlers
    const handleSaveReferralPrize = async () => {
        try {
            setModalLoading(true);
            await updateReferralPrize(parseFloat(referralPrize));
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Driver search config handlers
    const handleSaveDriverSearchConfig = async () => {
        try {
            setModalLoading(true);
            await updateDriverSearchConfig({
                initialDriverBatch: Number(driverSearchConfig.initialDriverBatch),
                nextDriverBatch: Number(driverSearchConfig.nextDriverBatch),
                nextDriverDelaySeconds: Number(driverSearchConfig.nextDriverDelaySeconds),
            });
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Joining prize handlers
    const handleSaveJoiningPrize = async () => {
        try {
            setModalLoading(true);
            await updateJoiningPrize(Boolean(joiningPrizeEnabled));
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    const addEmergencyNumber = () => {
        setEmergencyNumbers([...emergencyNumbers, { name: '', phoneNumber: '' }]);
    };

    const updateEmergencyNumber = (index, field, value) => {
        const updated = [...emergencyNumbers];
        updated[index][field] = value;
        setEmergencyNumbers(updated);
    };

    const removeEmergencyNumber = (index) => {
        setEmergencyNumbers(emergencyNumbers.filter((_, i) => i !== index));
    };

    const handleSaveEmergencyNumbers = async () => {
        try {
            setModalLoading(true);
            const cleaned = emergencyNumbers
                .map((item) => ({
                    name: (item.name || '').trim(),
                    phoneNumber: (item.phoneNumber || '').trim(),
                }))
                .filter((item) => item.name && item.phoneNumber);

            await updateEmergencyNumbers(cleaned);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    const updateAppUpdateField = (platform, field, value) => {
        setAppUpdateConfig((prev) => ({
            ...prev,
            [platform]: {
                ...(prev[platform] || {}),
                [field]: value,
            },
        }));
    };

    const handleSaveAppUpdateConfig = async () => {
        try {
            setModalLoading(true);
            const payload = {
                forceUpdate: Boolean(appUpdateConfig.forceUpdate),
                message: (appUpdateConfig.message || '').trim() || 'يوجد تحديث إجباري للتطبيق. يرجى التحديث للمتابعة.',
                android: {
                    minimumVersion: (appUpdateConfig.android?.minimumVersion || '').trim(),
                    minimumBuild: (appUpdateConfig.android?.minimumBuild || '').trim(),
                    storeUrl: (appUpdateConfig.android?.storeUrl || '').trim(),
                },
                ios: {
                    minimumVersion: (appUpdateConfig.ios?.minimumVersion || '').trim(),
                    minimumBuild: (appUpdateConfig.ios?.minimumBuild || '').trim(),
                    storeUrl: (appUpdateConfig.ios?.storeUrl || '').trim(),
                },
            };

            await updateAppUpdateConfig(payload);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    const handleSaveAppTermsConfig = async () => {
        try {
            setModalLoading(true);
            const payload = {
                userTerms: (appTermsConfig.userTerms || '').trim() || DEFAULT_SIGNUP_TERMS,
                driverTerms: (appTermsConfig.driverTerms || '').trim() || DEFAULT_SIGNUP_TERMS,
            };

            await updateAppTermsConfig(payload);
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    // Package handlers
    const handleEditPackage = (pkg) => {
        setPackageForm({
            _id: pkg._id,
            name: pkg.name,
            packageType: pkg.packageType || 'standard',
            price: pkg.price,
            durationInDays: pkg.durationInDays,
            maxTrips: pkg.maxTrips || '',
            isThereDiscount: pkg.isThereDiscount,
            priceAfterDiscount: pkg.priceAfterDiscount || '',
            packageImage: pkg.packageImage,
            description: Array.isArray(pkg.description) ? pkg.description.join('\n') : '',
            badgeLabel: pkg.badgeLabel || '',
            statusText: pkg.statusText || '',
            statusTone: pkg.statusTone || 'info',
            ctaText: pkg.ctaText || 'اشتراك الان',
            isActive: pkg.isActive !== undefined ? pkg.isActive : true
        });
        openModal('packages');
    };

    const handleDeletePackage = async (packageId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذه الباقة؟')) {
            return;
        }
        try {
            await deletePackage(packageId);
        } catch (error) {
            console.error(error);
        }
    };

    const handleSavePackage = async () => {
        try {
            setModalLoading(true);
            
            // Use the uploaded image if available, otherwise use the existing URL
            let imageToSave = packageForm.packageImage;
            if (packageImageFile.length > 0) {
                imageToSave = packageImageFile[0];
            }
            
            const packageData = {
                name: packageForm.name,
                packageType: packageForm.packageType,
                price: parseFloat(packageForm.price),
                durationInDays: parseInt(packageForm.durationInDays),
                maxTrips: packageForm.maxTrips === '' ? null : parseInt(packageForm.maxTrips),
                isThereDiscount: packageForm.isThereDiscount,
                priceAfterDiscount: packageForm.isThereDiscount ? parseFloat(packageForm.priceAfterDiscount) : undefined,
                packageImage: imageToSave,
                description: (packageForm.description || '').split('\n').map(t => t.trim()).filter(Boolean),
                badgeLabel: packageForm.badgeLabel || undefined,
                statusText: packageForm.statusText || undefined,
                statusTone: packageForm.statusTone || undefined,
                ctaText: packageForm.ctaText || undefined,
                isActive: packageForm.isActive
            };

            if (packageForm._id) {
                await updatePackage(packageForm._id, packageData);
            } else {
                await createPackage(packageData);
            }
            closeModal();
        } catch (error) {
            console.error(error);
        } finally {
            setModalLoading(false);
        }
    };

    if (isLoading) {
        return (
            <section className='container settings'>
                <div className="loading">جاري التحميل...</div>
            </section>
        );
    }

    return (
        <section className='container settings'>
            <h1 className="TXT-heading color-normal">الإعدادات</h1>
            <p className="TXT-normal color-light">إدارة إعدادات التطبيق</p>

            <div className="settings-grid">
                {/* Price Ranges Card */}
                <div className="setting-card" onClick={() => openModal('priceRanges')}>
                    <div className="card-icon">
                        <FaMoneyBillWave />
                    </div>
                    <h3>حساب سعر الرحلة</h3>
                    <p>إدارة نطاقات الأسعار حسب المسافة</p>
                    <div className="card-value">
                        {priceRanges.length} نطاق
                    </div>
                </div>

                {/* Max Search Range Card */}
                <div className="setting-card" onClick={() => openModal('maxSearchRange')}>
                    <div className="card-icon">
                        <FaMapMarkerAlt />
                    </div>
                    <h3>المسافة القصوى للبحث</h3>
                    <p>أقصى مسافة يمكن للمستخدم البحث فيها عن سائق</p>
                    <div className="card-value">
                        {maxSearchRange} كم
                    </div>
                </div>

                {/* Referral Prize Card */}
                <div className="setting-card" onClick={() => openModal('referralPrize')}>
                    <div className="card-icon">
                        <FaGift />
                    </div>
                    <h3>جائزة الإحالة</h3>
                    <p>المبلغ المكتسب عند دعوة صديق كسائق</p>
                    <div className="card-value">
                        {referralPrize} د.ل
                    </div>
                </div>

                {/* Joining Prize Card */}
                <div className="setting-card" onClick={() => openModal('joiningPrize')}>
                    <div className="card-icon">
                        <FaHandshake />
                    </div>
                    <h3>جائزة الانضمام</h3>
                    <p>منح أول اشتراك مجاني عند الرصيد الكافي</p>
                    <div className="card-value">
                        {joiningPrizeEnabled ? 'مفعّلة' : 'غير مفعّلة'}
                    </div>
                </div>

                {/* Emergency Numbers Card */}
                <div className="setting-card" onClick={() => openModal('emergencyNumbers')}>
                    <div className="card-icon">
                        <FaPhoneAlt />
                    </div>
                    <h3>أرقام الطوارئ</h3>
                    <p>إدارة قائمة التواصل العاجل</p>
                    <div className="card-value">
                        {emergencyCount} رقم
                    </div>
                </div>

                {/* Packages Card */}
                <div className="setting-card" onClick={() => openModal('packages')}>
                    <div className="card-icon">
                        <FaBox />
                    </div>
                    <h3>الباقات</h3>
                    <p>إدارة باقات الاشتراك</p>
                    <div className="card-value">
                        {packages.length} باقة
                    </div>
                </div>

                {/* Repair Shops Card */}
                <div className="setting-card" onClick={() => openModal('repairShops')}>
                    <div className="card-icon">
                        <FaTools />
                    </div>
                    <h3>ورش الإصلاح</h3>
                    <p>إدارة ورش الإصلاح المعروضة للمستخدم</p>
                    <div className="card-value">
                        {repairShops.length} ورشة
                    </div>
                </div>

                {/* Driver Search Config Card */}
                <div className="setting-card" onClick={() => openModal('driverSearchConfig')}>
                    <div className="card-icon">
                        <FaTools />
                    </div>
                    <h3>إعدادات البحث عن السائقين</h3>
                    <p>عدد السائقين في كل دفعة إشعار والوقت بينها</p>
                    <div className="card-value">
                        {driverSearchConfig.initialDriverBatch} ← {driverSearchConfig.nextDriverBatch} كل {driverSearchConfig.nextDriverDelaySeconds}ث
                    </div>
                </div>

                <div className="setting-card" onClick={() => openModal('appUpdate')}>
                    <div className="card-icon">
                        <FaCloudDownloadAlt />
                    </div>
                    <h3>التحديث الإجباري</h3>
                    <p>إدارة شروط ورسالة التحديث ومتاجر التحميل</p>
                    <div className="card-value">
                        {appUpdateConfig.forceUpdate ? 'مفعّل' : 'متوقف'}
                    </div>
                </div>

                <div className="setting-card" onClick={() => openModal('appTerms')}>
                    <div className="card-icon">
                        <FaEdit />
                    </div>
                    <h3>شروط التسجيل</h3>
                    <p>إدارة النصوص المعروضة للمستخدم والسائق</p>
                    <div className="card-value">
                        مفصولة حسب الدور
                    </div>
                </div>
            </div>

            {activeModal === 'driverSearchConfig' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>إعدادات البحث عن السائقين</h2>
                        <p className="modal-hint">عند إنشاء طلب جديد، يتم إشعار أقرب X سائق فوراً، ثم إشعار Y سائق كل Z ثانية حتى يتم تغطية جميع السائقين في النطاق.</p>

                        <div className="input-group">
                            <label>عدد السائقين في الدفعة الأولى</label>
                            <input
                                type="number"
                                min="1"
                                value={driverSearchConfig.initialDriverBatch}
                                onChange={(e) => setDriverSearchConfig((prev) => ({ ...prev, initialDriverBatch: e.target.value }))}
                                placeholder="مثال: 15"
                            />
                        </div>

                        <div className="input-group">
                            <label>عدد السائقين في كل دفعة تالية</label>
                            <input
                                type="number"
                                min="1"
                                value={driverSearchConfig.nextDriverBatch}
                                onChange={(e) => setDriverSearchConfig((prev) => ({ ...prev, nextDriverBatch: e.target.value }))}
                                placeholder="مثال: 5"
                            />
                        </div>

                        <div className="input-group">
                            <label>وقت الانتظار بين الدفعات (بالثواني)</label>
                            <input
                                type="number"
                                min="1"
                                value={driverSearchConfig.nextDriverDelaySeconds}
                                onChange={(e) => setDriverSearchConfig((prev) => ({ ...prev, nextDriverDelaySeconds: e.target.value }))}
                                placeholder="مثال: 20"
                            />
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveDriverSearchConfig} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeModal === 'appUpdate' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>إعدادات التحديث الإجباري</h2>
                        <p className="modal-hint">عند تفعيل الخيار: سيتم تطبيق الحد الأدنى للنسخة/البيلد لكل منصة. إذا كانت الحقول فارغة سيتم فرض التحديث على الجميع.</p>

                        <div className="input-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={Boolean(appUpdateConfig.forceUpdate)}
                                    onChange={(e) => setAppUpdateConfig((prev) => ({ ...prev, forceUpdate: e.target.checked }))}
                                />
                                تفعيل التحديث الإجباري
                            </label>
                        </div>

                        <div className="input-group">
                            <label>رسالة التحديث</label>
                            <textarea
                                rows={3}
                                value={appUpdateConfig.message}
                                onChange={(e) => setAppUpdateConfig((prev) => ({ ...prev, message: e.target.value }))}
                            />
                        </div>

                        <h3 style={{ marginTop: 10 }}>Android</h3>
                        <div className="input-row">
                            <div className="input-group">
                                <label>أدنى نسخة</label>
                                <input
                                    type="text"
                                    value={appUpdateConfig.android.minimumVersion}
                                    onChange={(e) => updateAppUpdateField('android', 'minimumVersion', e.target.value)}
                                    placeholder="مثال: 1.0.2"
                                />
                            </div>
                            <div className="input-group">
                                <label>أدنى Build</label>
                                <input
                                    type="text"
                                    value={appUpdateConfig.android.minimumBuild}
                                    onChange={(e) => updateAppUpdateField('android', 'minimumBuild', e.target.value)}
                                    placeholder="مثال: 28"
                                />
                            </div>
                        </div>
                        <div className="input-group">
                            <label>رابط المتجر (Android)</label>
                            <input
                                type="text"
                                value={appUpdateConfig.android.storeUrl}
                                onChange={(e) => updateAppUpdateField('android', 'storeUrl', e.target.value)}
                                placeholder="https://play.google.com/store/apps/details?id=com.jahez.ly"
                            />
                        </div>

                        <h3 style={{ marginTop: 10 }}>iOS</h3>
                        <div className="input-row">
                            <div className="input-group">
                                <label>أدنى نسخة</label>
                                <input
                                    type="text"
                                    value={appUpdateConfig.ios.minimumVersion}
                                    onChange={(e) => updateAppUpdateField('ios', 'minimumVersion', e.target.value)}
                                    placeholder="مثال: 1.0.3"
                                />
                            </div>
                            <div className="input-group">
                                <label>أدنى Build</label>
                                <input
                                    type="text"
                                    value={appUpdateConfig.ios.minimumBuild}
                                    onChange={(e) => updateAppUpdateField('ios', 'minimumBuild', e.target.value)}
                                    placeholder="مثال: 29"
                                />
                            </div>
                        </div>
                        <div className="input-group">
                            <label>رابط المتجر (iOS)</label>
                            <input
                                type="text"
                                value={appUpdateConfig.ios.storeUrl}
                                onChange={(e) => updateAppUpdateField('ios', 'storeUrl', e.target.value)}
                                placeholder="https://apps.apple.com/..."
                            />
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveAppUpdateConfig} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeModal === 'appTerms' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>شروط التسجيل (مستخدم / سائق)</h2>
                        <p className="modal-hint">هذه النصوص تظهر داخل نافذة الموافقة على الشروط في شاشة إنشاء الحساب.</p>

                        <div className="input-group">
                            <label>شروط المستخدم</label>
                            <textarea
                                rows={10}
                                value={appTermsConfig.userTerms}
                                onChange={(e) => setAppTermsConfig((prev) => ({ ...prev, userTerms: e.target.value }))}
                            />
                        </div>

                        <div className="input-group">
                            <label>شروط السائق</label>
                            <textarea
                                rows={10}
                                value={appTermsConfig.driverTerms}
                                onChange={(e) => setAppTermsConfig((prev) => ({ ...prev, driverTerms: e.target.value }))}
                            />
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveAppTermsConfig} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Price Ranges Modal */}
            {activeModal === 'priceRanges' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content settings-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>نطاقات الأسعار</h2>
                        <div className="price-ranges-list">
                            {priceRanges.map((range, index) => (
                                <div key={index} className="price-range-item">
                                    <div className="range-inputs">
                                        <div className="input-group">
                                            <label>من (كم)</label>
                                            <input
                                                type="number"
                                                value={range.fromKm}
                                                onChange={(e) => updatePriceRange(index, 'fromKm', e.target.value)}
                                                min="0"
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label>إلى (كم)</label>
                                            <input
                                                type="number"
                                                value={range.toKm}
                                                onChange={(e) => updatePriceRange(index, 'toKm', e.target.value)}
                                                min="0"
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label>السعر الابتدائي (د.ل)</label>
                                            <input
                                                type="number"
                                                value={range.startingPrice}
                                                onChange={(e) => updatePriceRange(index, 'startingPrice', e.target.value)}
                                                min="0"
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label>السعر لكل كم (د.ل)</label>
                                            <input
                                                type="number"
                                                value={range.pricePerKm}
                                                onChange={(e) => updatePriceRange(index, 'pricePerKm', e.target.value)}
                                                min="0"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        className="remove-btn"
                                        onClick={() => removePriceRange(index)}
                                        disabled={priceRanges.length === 1}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button className="add-range-btn" onClick={addPriceRange}>
                            <FaPlus /> إضافة نطاق
                        </button>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSavePriceRanges} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Max Search Range Modal */}
            {activeModal === 'maxSearchRange' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>المسافة القصوى للبحث</h2>
                        <div className="input-group">
                            <label>المسافة القصوى (كم)</label>
                            <input
                                type="number"
                                value={maxSearchRange}
                                onChange={(e) => setMaxSearchRange(e.target.value)}
                                min="1"
                                placeholder="أدخل المسافة القصوى"
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveMaxSearchRange} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Referral Prize Modal */}
            {activeModal === 'referralPrize' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>جائزة الإحالة</h2>
                        <div className="input-group">
                            <label>المبلغ المكتسب (د.ل)</label>
                            <input
                                type="number"
                                value={referralPrize}
                                onChange={(e) => setReferralPrize(e.target.value)}
                                min="0"
                                placeholder="أدخل المبلغ"
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveReferralPrize} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Joining Prize Modal */}
            {activeModal === 'joiningPrize' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>جائزة الانضمام</h2>
                        <p className="modal-hint">عند تفعيلها: إذا كان رصيد السائق يغطي ثمن الباقة الأولى، يتم تفعيل الاشتراك دون خصم الرصيد لمرة واحدة فقط.</p>
                        <div className="input-group checkbox-group">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={joiningPrizeEnabled}
                                    onChange={(e) => setJoiningPrizeEnabled(e.target.checked)}
                                />
                                تفعيل جائزة الانضمام
                            </label>
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveJoiningPrize} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Emergency Numbers Modal */}
            {activeModal === 'emergencyNumbers' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>أرقام الطوارئ</h2>
                        <p className="modal-hint">سيتم عرض هذه الأرقام في قوائم المستخدمين والسائقين.</p>
                        <div className="price-ranges-list">
                            {emergencyNumbers.map((item, index) => (
                                <div key={index} className="price-range-item">
                                    <div className="range-inputs">
                                        <div className="input-group">
                                            <label>الاسم</label>
                                            <input
                                                type="text"
                                                value={item.name}
                                                onChange={(e) => updateEmergencyNumber(index, 'name', e.target.value)}
                                                placeholder="مثال: الإسعاف"
                                            />
                                        </div>
                                        <div className="input-group">
                                            <label>رقم الهاتف</label>
                                            <input
                                                type="text"
                                                value={item.phoneNumber}
                                                onChange={(e) => updateEmergencyNumber(index, 'phoneNumber', e.target.value)}
                                                placeholder="09XXXXXXXX"
                                            />
                                        </div>
                                    </div>
                                    <button
                                        className="remove-btn"
                                        onClick={() => removeEmergencyNumber(index)}
                                        disabled={emergencyNumbers.length === 1}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button className="add-range-btn" onClick={addEmergencyNumber}>
                            <FaPlus /> إضافة رقم
                        </button>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal} disabled={modalLoading}>
                                إلغاء
                            </button>
                            <button className="submit-btn" onClick={handleSaveEmergencyNumbers} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : 'حفظ'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Packages Modal */}
            {activeModal === 'packages' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content packages-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>إدارة الباقات</h2>
                        
                        {/* Package Form */}
                        <div className="package-form">
                            <div className="input-group">
                                <label>اسم الباقة</label>
                                <input
                                    type="text"
                                    value={packageForm.name}
                                    onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                                    placeholder="أدخل اسم الباقة"
                                />
                            </div>
                            <div className="input-group">
                                <label>نوع الباقة</label>
                                <select
                                    value={packageForm.packageType}
                                    onChange={(e) => setPackageForm({ ...packageForm, packageType: e.target.value })}
                                >
                                    <option value="standard">عادية</option>
                                    <option value="joining">باقة الانضمام</option>
                                </select>
                            </div>
                            <div className="input-row">
                                <div className="input-group">
                                    <label>السعر (د.ل)</label>
                                    <input
                                        type="number"
                                        value={packageForm.price}
                                        onChange={(e) => setPackageForm({ ...packageForm, price: e.target.value })}
                                        min="0"
                                        placeholder="السعر"
                                    />
                                </div>
                                <div className="input-group">
                                    <label>المدة (أيام)</label>
                                    <input
                                        type="number"
                                        value={packageForm.durationInDays}
                                        onChange={(e) => setPackageForm({ ...packageForm, durationInDays: e.target.value })}
                                        min="1"
                                        placeholder="المدة"
                                    />
                                </div>
                                <div className="input-group">
                                    <label>حد الرحلات المكتملة</label>
                                    <input
                                        type="number"
                                        value={packageForm.maxTrips}
                                        onChange={(e) => setPackageForm({ ...packageForm, maxTrips: e.target.value })}
                                        min="1"
                                        placeholder="اتركه فارغاً لغير المحدود"
                                    />
                                </div>
                            </div>
                            <div className="input-group">
                                <label>وصف الباقة (سطر لكل نقطة)</label>
                                <textarea
                                    value={packageForm.description}
                                    onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                                    rows={3}
                                    placeholder="أدخل وصف الباقة"
                                />
                            </div>
                            <div className="input-row">
                                <div className="input-group">
                                    <label>عنوان الشارة (اختياري)</label>
                                    <input
                                        type="text"
                                        value={packageForm.badgeLabel}
                                        onChange={(e) => setPackageForm({ ...packageForm, badgeLabel: e.target.value })}
                                        placeholder="مثال: 3"
                                    />
                                </div>
                                <div className="input-group">
                                    <label>نص الحالة</label>
                                    <input
                                        type="text"
                                        value={packageForm.statusText}
                                        onChange={(e) => setPackageForm({ ...packageForm, statusText: e.target.value })}
                                        placeholder="مثال: متاح"
                                    />
                                </div>
                            </div>
                            <div className="input-row">
                                <div className="input-group">
                                    <label>درجة الحالة</label>
                                    <select
                                        value={packageForm.statusTone}
                                        onChange={(e) => setPackageForm({ ...packageForm, statusTone: e.target.value })}
                                    >
                                        <option value="info">اعتيادي</option>
                                        <option value="success">نجاح</option>
                                        <option value="warning">تحذير</option>
                                        <option value="danger">خطر</option>
                                    </select>
                                </div>
                                <div className="input-group">
                                    <label>نص الزر</label>
                                    <input
                                        type="text"
                                        value={packageForm.ctaText}
                                        onChange={(e) => setPackageForm({ ...packageForm, ctaText: e.target.value })}
                                        placeholder="مثال: اشتراك الان"
                                    />
                                </div>
                            </div>
                            <div className="input-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={packageForm.isThereDiscount}
                                        onChange={(e) => setPackageForm({ ...packageForm, isThereDiscount: e.target.checked })}
                                    />
                                    يوجد خصم
                                </label>
                            </div>
                            <div className="input-group checkbox-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={packageForm.isActive}
                                        onChange={(e) => setPackageForm({ ...packageForm, isActive: e.target.checked })}
                                    />
                                    الباقة فعّالة
                                </label>
                            </div>
                            {packageForm.isThereDiscount && (
                                <div className="input-group">
                                    <label>السعر بعد الخصم (د.ل)</label>
                                    <input
                                        type="number"
                                        value={packageForm.priceAfterDiscount}
                                        onChange={(e) => setPackageForm({ ...packageForm, priceAfterDiscount: e.target.value })}
                                        min="0"
                                        placeholder="السعر بعد الخصم"
                                    />
                                </div>
                            )}
                            <div className="input-group">
                                <label>صورة الباقة</label>
                                <DropImage setAcceptedFile={setPackageImageFile} maxFiles={1}>
                                    <div className="drop-zone">
                                        {packageImageFile.length > 0 ? (
                                            <div className="image-preview">
                                                <img src={packageImageFile[0]} alt="Package preview" />
                                                <p>اضغط أو اسحب لتغيير الصورة</p>
                                            </div>
                                        ) : packageForm.packageImage ? (
                                            <div className="image-preview">
                                                <img src={packageForm.packageImage} alt="Package preview" />
                                                <p>اضغط أو اسحب لتغيير الصورة</p>
                                            </div>
                                        ) : (
                                            <div className="drop-placeholder">
                                                <FaPlus />
                                                <p>اضغط أو اسحب الصورة هنا</p>
                                                <span>PNG, JPG, GIF حتى 5MB</span>
                                            </div>
                                        )}
                                    </div>
                                </DropImage>
                            </div>
                            <button className="save-package-btn" onClick={handleSavePackage} disabled={modalLoading}>
                                {modalLoading ? <span className="loading-spinner"></span> : (packageForm._id ? 'تحديث الباقة' : 'إضافة باقة')}
                            </button>
                        </div>

                        {/* Packages List */}
                        <div className="packages-list">
                            <h3>الباقات الحالية</h3>
                            {packages.map((pkg) => (
                                <div key={pkg._id} className="package-item">
                                    <div className="package-info">
                                        <h4>{pkg.name}</h4>
                                        <p>النوع: {pkg.packageType === 'joining' ? 'باقة الانضمام' : 'عادية'}</p>
                                        <p>
                                            {pkg.isThereDiscount ? (
                                                <>
                                                    <span className="original-price">{pkg.price} د.ل</span>
                                                    <span className="discount-price">{pkg.priceAfterDiscount} د.ل</span>
                                                </>
                                            ) : (
                                                <span>{pkg.price} د.ل</span>
                                            )}
                                            {' - '}
                                            {pkg.durationInDays} يوم
                                            {' - '}
                                            {pkg.maxTrips ? `${pkg.maxTrips} رحلات` : 'رحلات غير محدودة'}
                                            {pkg.statusText ? ` - ${pkg.statusText}` : ''}
                                            {pkg.isActive === false ? ' (غير فعّالة)' : ''}
                                        </p>
                                        {pkg.badgeLabel ? <p>شارة: {pkg.badgeLabel}</p> : null}
                                        {Array.isArray(pkg.description) && pkg.description.length > 0 && (
                                            <ul className="package-desc-list">
                                                {pkg.description.map((line, idx) => (
                                                    <li key={idx}>{line}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="package-actions">
                                        <button className="edit-btn" onClick={() => handleEditPackage(pkg)}>
                                            <FaEdit />
                                        </button>
                                        <button className="delete-btn" onClick={() => handleDeletePackage(pkg._id)}>
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal}>
                                إغلاق
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Repair Shops Modal */}
            {activeModal === 'repairShops' && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>ورش الإصلاح</h2>
                        <form className="repair-form" onSubmit={handleSaveRepairShop}>
                            <div className="input-group">
                                <label>اسم الورشة</label>
                                <input
                                    type="text"
                                    value={repairForm.name}
                                    onChange={(e) => setRepairForm({ ...repairForm, name: e.target.value })}
                                    required
                                    placeholder="مثال: ورشة الصيانة السريعة"
                                />
                            </div>
                            <div className="input-group">
                                <label>العنوان</label>
                                <input
                                    type="text"
                                    value={repairForm.address}
                                    onChange={(e) => setRepairForm({ ...repairForm, address: e.target.value })}
                                    placeholder="المدينة، الشارع"
                                />
                            </div>
                            <div className="input-group">
                                <label>التخصصات (اختياري)</label>
                                <input
                                    type="text"
                                    value={repairForm.specialties}
                                    onChange={(e) => setRepairForm({ ...repairForm, specialties: e.target.value })}
                                    placeholder="مثال: إطارات، كهرباء، هيكل"
                                />
                            </div>
                            <div className="input-group">
                                <label>رقم الهاتف (اختياري)</label>
                                <input
                                    type="text"
                                    value={repairForm.phone}
                                    onChange={(e) => setRepairForm({ ...repairForm, phone: e.target.value })}
                                    placeholder="09XXXXXXXX"
                                />
                            </div>

                            <div className="input-group" style={{ position: 'relative' }}>
                                <label>ابحث عن الموقع أو اختره من الخريطة</label>
                                <input
                                    type="text"
                                    value={locationQuery}
                                    onChange={(e) => searchLocations(e.target.value)}
                                    placeholder="مثال: Tripoli, Libya"
                                />
                                {locationQuery.trim().length >= 3 && (
                                    <div className="suggestions-dropdown">
                                        {locationLoading ? (
                                            <div className="suggestion-item" style={{ color: '#666' }}>جاري البحث...</div>
                                        ) : locationSuggestions.length === 0 ? (
                                            <div className="suggestion-item" style={{ color: '#666' }}>
                                                {locationHint || 'لا توجد نتائج'}
                                            </div>
                                        ) : (
                                            locationSuggestions.map((item, idx) => (
                                                <div
                                                    key={idx}
                                                    className="suggestion-item"
                                                    onClick={() => handleSelectSuggestion(item)}
                                                >
                                                    <div>{item.mainText || item.label}</div>
                                                    {item.secondaryText ? <div style={{ color: '#777', fontSize: 12 }}>{item.secondaryText}</div> : null}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="map-picker" style={{ height: 320, marginTop: 10, borderRadius: 8, overflow: 'hidden' }}>
                                <MapContainer center={mapCenter} zoom={12} style={{ height: '100%', width: '100%' }}>
                                    <TileLayer
                                        url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                                        subdomains={["mt0", "mt1", "mt2", "mt3"]}
                                        attribution='Map data © Google'
                                    />
                                    <ClickHandler onSelect={setCoords} />
                                    {Number.isFinite(Number(repairForm.latitude)) && Number.isFinite(Number(repairForm.longitude)) && (
                                        <Marker position={[Number(repairForm.latitude), Number(repairForm.longitude)]} icon={markerIcon} />
                                    )}
                                </MapContainer>
                            </div>

                            <div className="coords-row" style={{ display: 'flex', gap: 12, marginTop: 10 }}>
                                <div className="input-group" style={{ flex: 1 }}>
                                    <label>خط العرض</label>
                                    <input
                                        type="number"
                                        value={repairForm.latitude}
                                        onChange={(e) => setCoords({ latitude: Number(e.target.value), longitude: Number(repairForm.longitude) })}
                                        step="0.000001"
                                        required
                                    />
                                </div>
                                <div className="input-group" style={{ flex: 1 }}>
                                    <label>خط الطول</label>
                                    <input
                                        type="number"
                                        value={repairForm.longitude}
                                        onChange={(e) => setCoords({ latitude: Number(repairForm.latitude), longitude: Number(e.target.value) })}
                                        step="0.000001"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="modal-actions" style={{ justifyContent: 'flex-start', gap: 10 }}>
                                <button type="submit" className="submit-btn" disabled={repairShopsSaving}>
                                    {repairShopsSaving ? <span className="loading-spinner"></span> : (editingRepairId ? 'تحديث الورشة' : 'إضافة ورشة')}
                                </button>
                            </div>
                        </form>

                        <div className="repair-shops-list">
                            {repairShopsLoading ? (
                                <div className="loading">جاري تحميل الورش...</div>
                            ) : repairShops.length === 0 ? (
                                <div className="empty">لا توجد ورش مضافة بعد</div>
                            ) : (
                                repairShops.map((shop) => (
                                    <div key={shop._id} className="repair-card">
                                        <div className="repair-card__top">
                                            <div className="repair-card__info">
                                                <div className="repair-card__name">{shop.name}</div>
                                                {shop.specialties ? <div className="repair-card__address" style={{ color: 'var(--primary-color)', fontStyle: 'italic' }}>{shop.specialties}</div> : null}
                                                {shop.address ? <div className="repair-card__address">{shop.address}</div> : null}
                                                {shop.phone ? <div className="repair-card__address" style={{ direction: 'ltr', textAlign: 'left' }}>{shop.phone}</div> : null}
                                            </div>
                                            <div className="repair-card__coords">
                                                <span>خط العرض</span>
                                                <strong>{shop?.location?.latitude}</strong>
                                                <span>خط الطول</span>
                                                <strong>{shop?.location?.longitude}</strong>
                                            </div>
                                        </div>
                                        <div className="repair-card__actions">
                                            <button className="edit-btn" type="button" onClick={() => startEditRepair(shop)}>
                                                <FaEdit /> تعديل
                                            </button>
                                            <button className="delete-btn" type="button" onClick={() => deleteShop(shop._id)} disabled={repairShopsSaving}>
                                                <FaTrash /> حذف
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={closeModal}>
                                إغلاق
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};